import { useCallback, useEffect, useRef, useState, CSSProperties } from 'react';
import { Select, Spin } from 'antd';

export type AutocompleteOption = {
  label: string;
  value: string;
};

type AutocompleteSelectProps = {
  mode?: 'multiple';
  value?: string | string[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  fetchOptions: (query: string) => Promise<AutocompleteOption[]>;
  onChange: (value: string | string[]) => void;
  debounceMs?: number;
  style?: CSSProperties;
  dependencies?: unknown[];
};

const selectPopupProps = {
  listHeight: 240,
  virtual: false as const,
  getPopupContainer: () => document.body,
  popupMatchSelectWidth: true,
};

export function AutocompleteSelect({
  mode,
  value,
  placeholder = 'Selecione',
  disabled = false,
  allowClear = true,
  fetchOptions,
  onChange,
  debounceMs = 350,
  style,
  dependencies = [],
}: AutocompleteSelectProps) {
  const [options, setOptions] = useState<AutocompleteOption[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchRef = useRef(fetchOptions);
  fetchRef.current = fetchOptions;

  const load = useCallback(async (query: string) => {
    setLoading(true);
    try {
      const data = await fetchRef.current(query);
      setOptions(data);
    } catch (e) {
      console.error(e);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearch = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(query);
    }, debounceMs);
  };

  const normalizedValue =
    value === '' || (Array.isArray(value) && value.length === 0)
      ? undefined
      : value;

  return (
    <Select
      mode={mode}
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      value={normalizedValue}
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      onChange={(val) => {
        if (mode === 'multiple') {
          onChange(Array.isArray(val) ? val.map(String) : []);
          return;
        }
        onChange(val ? String(val) : '');
      }}
      onDropdownVisibleChange={(open) => {
        if (open && options.length === 0) load('');
      }}
      options={options}
      maxTagCount={mode === 'multiple' ? 'responsive' : undefined}
      notFoundContent={loading ? <Spin size="small" /> : 'Nenhum resultado'}
      style={{ width: '100%', ...style }}
      className={`filter-select${mode === 'multiple' ? ' filter-select-multi' : ''}`}
      {...selectPopupProps}
    />
  );
}
